//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import '@react/App.css'
import React from 'react'

import { LicenseInfo } from '@mui/x-license-pro'

import { EthersAdapter } from '@reown/appkit-adapter-ethers'
import {
  AppKitNetwork,
  avalanche,
  avalancheFuji,
  mainnet,
  plumeMainnet,
  plumeSepolia,
  sepolia,
} from '@reown/appkit/networks'
import { createAppKit, CreateAppKit } from '@reown/appkit/react'

import { initialChainState, locationToStageName } from '@react/redux/chainSlice'
import { otAssert } from '@shared/conversions'

import {
  chainConfigurationNameToChainConfiguration,
  convertToAppKitNetwork,
  findStageConfiguration,
} from '@shared/DeploymentConfigs'
import { ChainConfigName } from '@shared/DeploymentConfigTypes'
import { logOT } from '@shared/loggingShared'

export const appKitNetworks = [mainnet, avalanche, avalancheFuji, sepolia, plumeMainnet, plumeSepolia] as [
  AppKitNetwork,
  ...AppKitNetwork[],
]
//This needs to be set here?
LicenseInfo.setLicenseKey(
  '4aa820c74354bf0194df6f7dfa013a7fTz0xMTE4ODYsRT0xNzc3MDc1MTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
)
// type OtAppKitContextType = {
//   appKit: AppKit | undefined
// }

//  export const OtAppKitContext = createContext<OtAppKitContextType>({
//   appKit: undefined,
// })
const getAppKitConfig = (): CreateAppKit => {
  const forAdmin = location.hostname.startsWith('admin')
  const {
    stageConfiguration: { domain },
    chainConfiguration: { walletConnectProjectId, frontendProviderUrl, chainId },
  } = initialChainState

  otAssert(walletConnectProjectId, 'walletConnectProjectId is required')
  otAssert(frontendProviderUrl, 'frontendProviderUrl is required')

  const metadata = {
    name: 'OpenTrade',
    description: 'OpenTrade',
    url: forAdmin ? `https://${domain.replace('app', 'admin')}` : `https://${domain}`, // origin must match your domain & subdomain
    icons: [forAdmin ? `https://${domain.replace('app', 'admin')}/favicon.ico` : `https://${domain}/favicon.ico`],
  }

  // prettier-ignore
  const networks =  [mainnet, avalanche, avalancheFuji, sepolia, plumeMainnet, plumeSepolia] as [
    AppKitNetwork,
    ...AppKitNetwork[]
  ]

  const defaultNetwork = networks.find((network) => network.id === chainId)

  // Reorder networks to put default network first
  if (defaultNetwork) {
    const networkIndex = networks.findIndex((network) => network.id === defaultNetwork.id)
    if (networkIndex > 0) {
      networks.splice(networkIndex, 1)
      networks.unshift(defaultNetwork)
    }
  }

  logOT.info('Appkit INITIALIZE', defaultNetwork)

  const appKitConfig: CreateAppKit = {
    adapters: [new EthersAdapter()],
    networks,
    metadata,
    defaultNetwork,
    projectId: walletConnectProjectId,
    themeMode: 'light',
    features: {
      legalCheckbox: true,
      email: true, // default to true
      socials: ['google'],
      emailShowWallets: true,
      analytics: true, // Optional - defaults to your Cloud configuration
    },
    themeVariables: {
      '--w3m-accent': '#000000',
    },
    defaultAccountTypes: { eip155: 'eoa' },

    allWallets: 'SHOW',
    enableWalletConnect: true,
    enableNetworkSwitch: true,
    featuredWalletIds: [
      'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
      '225affb176778569276e484e1b92637ad061b01e13a048b35a9d280c3b58970f',
    ],
    // debug: true,

    termsConditionsUrl: 'https://opentrade.gitbook.io/opentrade-docs/legal/platform-terms-and-conditions',
    // siwx: new OtAuthSIWX(),
  }

  return appKitConfig
}

if (!location.hostname.startsWith('localhost')) {
  const appKit = createAppKit(getAppKitConfig())

  const appKitChainId = appKit.getChainId()

  let chainConfigName = localStorage.getItem('chainConfigurationName')
  if (!chainConfigName) {
    const locationStageName = locationToStageName(location)
    const stageConfig = findStageConfiguration(locationStageName)
    chainConfigName = stageConfig.chainConfigurations[0].chainConfigurationName
    localStorage.setItem('chainConfigurationName', chainConfigName)
  }
  if (chainConfigName) {
    const chainConfigurationLocalStorage = chainConfigurationNameToChainConfiguration(
      chainConfigName as ChainConfigName
    )
    logOT.info('AppKitWrapper chainConfigurationName', chainConfigurationLocalStorage.chainId, appKitChainId)

    if (chainConfigurationLocalStorage.chainId !== appKitChainId) {
      const appKitNetwork = convertToAppKitNetwork(chainConfigurationLocalStorage)

      logOT.info('AppKitWrapper chainConfigurationName  switchNetwork AT STARTUP', appKitNetwork)

      appKit.switchNetwork(appKitNetwork as any).then(() => {
        const newChainId = appKit.getChainId()
        logOT.info(
          'AppKitWrapper chainConfigurationName  switchNetwork AT FINISHED',
          chainConfigurationLocalStorage.chainId,
          chainConfigurationLocalStorage.chainConfigurationName,
          'from',
          appKitChainId,
          'to',
          newChainId
        )

        if (newChainId !== chainConfigurationLocalStorage.chainId) {
          logOT.info(
            'AppKitWrapper RELOAD CHAIN ID MISMATCH ON RELOAD',
            newChainId,
            chainConfigurationLocalStorage.chainId
          )

          window.location.href = '/'
        }
      })
    }
  }
}

export function AppKitWrapper({ children }: { children: React.ReactNode }): React.ReactNode {
  return <>{children}</>
}
